import { Waypoint } from '@omniafishing/core';
import _ from 'lodash';
import { MapRef } from 'react-map-gl';
import { getEnv } from '../../../../env';
import { isInteractiveWaterbodyFeature } from '../../map_types';

const env = getEnv();
export const truncateTo6Decimal = (num: number): number => {
  if (typeof num !== 'number') {
    return 0;
  }
  return Math.trunc(num * 1e6) / 1e6;
};

export const getLabel = (wt: Waypoint['waypoint_type']) => {
  const words = wt.replace(/_/g, ' ').split(' ');
  return words.map((w) => w.slice(0, 1).toUpperCase() + w.slice(1)).join(' ');
};

export const queryRenderedFeaturesAtPoint = (mapRef: MapRef, lat: number, lng: number) => {
  if (!mapRef) {
    return;
  }
  const map = mapRef.getMap();
  const point = map.project([lng, lat]);

  const features = map.queryRenderedFeatures(point);
  const interactiveWaterbodyFeature = features.find((f) =>
    isInteractiveWaterbodyFeature(f.properties)
  );
  const waterbody_id = interactiveWaterbodyFeature?.properties?.waterbody_id;
  return waterbody_id;
};

interface MapboxTileQueryResponse {
  type: string;
  features: mapboxgl.MapboxGeoJSONFeature[];
}

export const queryMapAtPoint = async (lat: number, lng: number) => {
  const url = `https://api.mapbox.com/v4/omniafishing.omnia-waterbodies/tilequery/${lng},${lat}.json?access_token=${env.MAPBOX_ACCESS_TOKEN}`;
  const response = await fetch(url);

  const data: MapboxTileQueryResponse = await response.json();
  const interactiveWaterbodyFeature = data.features.find((f) =>
    isInteractiveWaterbodyFeature(f.properties)
  );
  const waterbody_id = interactiveWaterbodyFeature?.properties?.waterbody_id;
  return waterbody_id;
};

export const debouncedGetWaterbodyIdAtPoint = _.debounce(
  (mapRef: MapRef, lat: number, lng: number, callback: (id: string) => void) => {
    const waterbody_id = queryRenderedFeaturesAtPoint(mapRef, lat, lng);
    return callback(waterbody_id);
  },
  200
);

export const DMStoDecimal = (degrees: number, minutes: number, seconds: number) => {
  // Ensure that minutes and seconds are added positively
  const totalDegrees = Math.abs(degrees) + minutes / 60 + seconds / 3600;

  // Apply the sign from the degrees directly
  return truncateTo6Decimal(degrees >= 0 ? totalDegrees : -totalDegrees);
};

export const decimalToDMS = (decimal: number) => {
  let degrees = Math.floor(Math.abs(decimal)); // Always positive
  const minutesFloat = (Math.abs(decimal) - degrees) * 60;
  let minutes = Math.floor(minutesFloat);
  let seconds = parseFloat(((minutesFloat - minutes) * 60).toFixed(2)); // Convert to a precise number

  // Handling the case where seconds are very close to 60
  if (seconds >= 60) {
    seconds = 0;
    minutes += 1;
  }

  // If minutes become 60 due to second overflow (rare case but possible)
  if (minutes >= 60) {
    minutes = 0;
    degrees += 1;
  }

  // Preserve the sign for degrees based on the original decimal
  const signedDegrees = decimal < 0 ? -degrees : degrees;

  return {
    degrees: signedDegrees,
    minutes,
    seconds,
  };
};
